import { CircleFontsForSizes, Sizes, TRANSITION_TIME, Types } from './constants';
import { Spacing, encodeIconSVG, getFocusStyles, themedFontFamily, withTheming } from '@doordash/design-language';
import styled, { css } from 'styled-components';
import CheckmarkIcon from './CheckmarkIcon';
import React from 'react';
import { getInnerRadioCircleDimensions } from './utils';
export var ToggleContainer = styled.div.withConfig({
  displayName: "styles__ToggleContainer",
  componentId: "sc-14r4qak-0"
})(["display:flex;user-select:none;flex-grow:1;flex-direction:column;"]);
export var InputContainer = styled.div.withConfig({
  displayName: "styles__InputContainer",
  componentId: "sc-14r4qak-1"
})(["", ";"], function (props) {
  return !props.isCustom && css(["height:", "px;display:flex;align-items:center;"], props.size);
});
export var ErrorMessageContainer = styled.div.withConfig({
  displayName: "styles__ErrorMessageContainer",
  componentId: "sc-14r4qak-2"
})(["margin-top:", "px;"], Spacing.XxSmall);

var encodedCheckStyling = function encodedCheckStyling(props) {
  var containerSize = props.themeSize; // Due to the asymmetrical shape of the icon, as well as the different icons used
  // between Small and Large sizes, this logic ensures the checkmark renders with enough
  // breathing room between the check tails and the surrounding Toggle component

  var checkmarkSize = containerSize - props.borderWidth * (props.size === Sizes.Small ? 3 : 4); //  Note: KEEP DOUBLE QUOTATIONS on lines 44 and 55 to wrap url content so that nested single quotations from
  //  `encodeIconSVG` don't result in an improperly formatted url string

  return css(["height:", "px;width:", ";", " background:url(\"data:image/svg+xml,", "\") no-repeat center;"], checkmarkSize, props.kind === Types.Toggle ? "".concat(containerSize / 2, "px") : "".concat(checkmarkSize, "px"), ''
  /* eslint-disable-next-line prettier/prettier */
  , encodeIconSVG( /*#__PURE__*/React.createElement(CheckmarkIcon, {
    size: props.size,
    fillColor: props.theme.Colors["".concat(props.kind === Types.Toggle ? 'Switch' : 'Toggle', "ForegroundOn").concat(props.disabled ? 'Disabled' : '')]
  })));
};

var memoizedCheckStyling = function memoizedCheckStyling() {
  var checkStylingCache = {};
  return function (props) {
    if (checkStylingCache.hasOwnProperty("".concat(props.size, "-").concat(props.disabled, "-").concat(props.kind))) {
      return checkStylingCache["".concat(props.size, "-").concat(props.disabled, "-").concat(props.kind)];
    }

    var result = encodedCheckStyling(props);
    checkStylingCache["".concat(props.size, "-").concat(props.disabled, "-").concat(props.kind)] = result;
    return result;
  };
};

var checkStyling = memoizedCheckStyling();
/**
 * makeCheck
 * Returns background checkmark styling using Prism `Check` Icon
 * to indicate "on" state for certain kinds of Toggle switches
 */

var makeCheck = function makeCheck(props) {
  return checkStyling(props);
};
/**
 * check
 * Checkmark CSS fragment for use as a pseudo-element in Checkmark
 */


export var check = css(["transition:transform ease-in-out ", ",opacity ease-in-out ", ";", " ", ";"], TRANSITION_TIME, TRANSITION_TIME, function (props) {
  return makeCheck(props);
}, function (props) {
  return props.checked && css(["opacity:1;", ";"], makeCheck(props));
});
/**
 * circle
 * Radio CSS fragment for use as a pseudo-element in RadioButton
 */

export var circle = css(["top:0;left:0;right:0;bottom:0;width:", "px;height:", "px;background-color:", ";transform:scale(0.5);transition:transform ease-in-out ", ",opacity ease-in-out ", ";border-radius:100%;", ";"], function (props) {
  return getInnerRadioCircleDimensions(props.themeSize);
}, function (props) {
  return getInnerRadioCircleDimensions(props.themeSize);
}, function (props) {
  if (props.kind === Types.Radio) {
    if (props.disabled) {
      return props.theme.Colors.ToggleBorderOnDisabled;
    }

    if (props.error) {
      return props.theme.Colors.TextError;
    }
  }

  if (props.disabled) {
    return props.theme.Colors.ToggleBackgroundOnDisabled;
  }

  return props.theme.Colors.ToggleBackgroundOn;
}, TRANSITION_TIME, TRANSITION_TIME, function (props) {
  return props.checked && css(["opacity:1;transform:scale(1);"]);
});
/**
 * toggle
 * Toggle circle CSS fragment for use as a pseudo-element in Toggle
 */

var getToggleBorderColor = function getToggleBorderColor(props) {
  if (props.disabled) {
    if (props.checked) {
      return props.theme.Colors.SwitchBorderOnDisabled;
    }

    return props.theme.Colors.SwitchBorderOffDisabled;
  }

  if (props.error) {
    return props.theme.Colors.TextError;
  }

  if (props.checked) {
    return props.theme.Colors.SwitchBorderOn;
  }

  return props.theme.Colors.SwitchBorderOff;
};

var getToggleCheckedStyles = function getToggleCheckedStyles(props) {
  if (props.checked) {
    return css(["opacity:1;transform:translateX(50%);background-color:", ";"], props.theme.Colors["SwitchForegroundOn".concat(props.disabled ? 'Disabled' : '')]);
  }

  return null;
};

var getToggleErrorStyles = function getToggleErrorStyles(props) {
  if (props.error) {
    return css(["border-color:", ";"], props.theme.Colors.TextError);
  }

  return null;
};

var getFontStylesFromCircleSize = function getFontStylesFromCircleSize(circleSize) {
  var fontObj = CircleFontsForSizes[circleSize];
  return "\n    font-size: ".concat(fontObj.size, "px;\n    font-family: ").concat(themedFontFamily(fontObj.themeFontFamily), ";\n    color: ").concat(fontObj.color, ";\n    font-weight: ").concat(fontObj.weight, ";\n    line-height: ").concat(fontObj.lineHeight, ";\n    letter-spacing: ").concat(fontObj.letterSpacing, ";\n    text-transform: ").concat(fontObj.textTransform, ";\n  ");
};

export var toggle = css(["top:0;left:0;right:0;bottom:0;width:", "px;height:", "px;transform:translateX(-50%);transition:transform ease-in-out ", ",opacity ease-in-out ", ";border-radius:100%;opacity:1;background-color:", ";border:", "px solid ", ";", " ", ""], function (props) {
  return props.themeSize;
}, function (props) {
  return props.themeSize;
}, TRANSITION_TIME, TRANSITION_TIME, function (props) {
  return props.theme.Colors["SwitchForegroundOff".concat(props.disabled ? 'Disabled' : '')];
}, function (props) {
  return props.borderWidth;
}, getToggleBorderColor, getToggleCheckedStyles, getToggleErrorStyles);
export var ToggleInput = withTheming(styled.input.withConfig({
  displayName: "styles__ToggleInput",
  componentId: "sc-14r4qak-3"
})(["display:flex;flex-shrink:0;align-items:center;justify-content:center;width:", ";height:", "px;border:", "px solid ", ";border-radius:", ";appearance:none;background-color:", ";position:relative;outline:none;margin:0;transition:all ease-in-out 0.2s;&:disabled{border-color:", ";background-color:", ";cursor:not-allowed;}&:focus,&:active{", ";}&::before,&::after{margin:auto;content:'';display:block;position:absolute;}&::after{opacity:0;", "  ", "  ", "}", " ", " ", ";", ";", "};"], function (props) {
  return props.kind === Types.Toggle ? "".concat(props.themeSize * 2, "px") : "".concat(props.themeSize, "px");
}, function (props) {
  return props.themeSize;
}, function (props) {
  return props.kind === Types.Circle ? '1px' : props.borderWidth;
}, function (props) {
  return props.theme.Colors.ToggleBorderOff;
}, function (props) {
  return props.borderRadius;
}, function (props) {
  return props.theme.Colors.ToggleBackgroundOff;
}, function (props) {
  return props.theme.Colors.ToggleBorderOffDisabled;
}, function (props) {
  return props.theme.Colors.ToggleBackgroundOffDisabled;
}, getFocusStyles(), function (props) {
  return props.kind === Types.Checkbox && check;
}, function (props) {
  return props.kind === Types.Radio && circle;
}, function (props) {
  return props.kind === Types.Toggle && toggle;
}, function (props) {
  return props.checked && css(["border-color:", ";background-color:", ";&:focus,&:active{border-color:", ";}&:disabled{border-color:", ";background-color:", ";}"], props.theme.Colors.ToggleBorderOn, props.kind === Types.Radio ? props.theme.Colors.ToggleBackgroundOff : props.theme.Colors.ToggleBackgroundOn, props.theme.Colors.ToggleBorderOn, props.theme.Colors.ToggleBorderOnDisabled, props.kind === Types.Radio ? props.theme.Colors.ToggleBackgroundOffDisabled : props.theme.Colors.ToggleBackgroundOnDisabled);
}, function (props) {
  return props.error && css(["border-color:", ";background-color:", ";&:focus,&:active{border-color:", ";}", ";"], props.theme.Colors.TextError, props.theme.Colors.ToggleBackgroundOff, props.theme.Colors.TextError, props.checked && css(["background-color:", ";"], props.kind === Types.Radio ? props.theme.Colors.ToggleBackgroundOff : props.theme.Colors.TextError));
}, function (props) {
  return props.kind === Types.Circle && css(["background-color:", ";border-color:", ";cursor:pointer;&::before{content:'", "';max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;", " padding:0 ", "px;}&:hover:not(:disabled){border-color:", ";background-color:", ";}", ";", ";"], props.theme.Colors.ButtonSecondaryToggleBackgroundOff, props.theme.Colors.ButtonSecondaryToggleBorderOff, props.label, getFontStylesFromCircleSize(props.size), Spacing.XxxSmall, props.checked ? props.theme.Colors.ButtonSecondaryToggleBorderOnHovered : props.theme.Colors.ButtonSecondaryToggleBorderOffHovered, props.checked ? props.theme.Colors.ButtonSecondaryToggleBackgroundHovered : props.theme.Colors.ButtonSecondaryToggleBackgroundOffHovered, props.checked && css(["background-color:", ";border-color:", ";color:", ";&::before{color:", ";}&:focus,&:active,&:hover:not(:disabled){border-color:", ";}"], props.theme.Colors.ButtonSecondaryToggleBackground, props.theme.Colors.ButtonSecondaryToggleBorderOn, props.theme.Colors.ButtonSecondaryToggleForeground, props.theme.Colors.ButtonSecondaryToggleForeground, props.theme.Colors.ButtonSecondaryToggleBorderOnHovered), props.error && css(["border-color:", ";&::before{color:", ";}&:focus,&:active,&:hover:not(:disabled){border-color:", ";}", ";"], props.theme.Colors.TextError, props.theme.Colors.TextError, props.theme.Colors.TextError, props.checked && css(["background-color:", ";"], props.theme.Colors.ButtonSecondaryToggleBackground)));
}, function (props) {
  return props.kind === Types.Toggle && css(["border-color:", ";background-color:", ";&:disabled{border-color:", " background-color:", ";}&:disabled:after{opacity:1;}", ";"], props.error ? props.theme.Colors.TextError : props.theme.Colors.SwitchBorderOff, props.theme.Colors.SwitchBackgroundOff, props.theme.Colors.SwitchBorderOffDisabled, props.theme.Colors.SwitchBackgroundOffDisabled, props.checked && css(["background-color:", ";border:", "px solid ", ";&:before{left:0;transform:translateX(50%);", ";}&:focus,&:active{border-color:", ";}&:disabled{border-color:", ";background-color:", ";}"], props.error ? props.theme.Colors.TextError : props.theme.Colors.SwitchBackgroundOn, props.borderWidth, props.error ? props.theme.Colors.TextError : props.theme.Colors.SwitchBorderOn, makeCheck(props), props.error ? props.theme.Colors.TextError : props.theme.Colors.SwitchBorderOn, props.theme.Colors.SwitchBorderOnDisabled, props.theme.Colors.SwitchBackgroundOnDisabled));
}, function (props) {
  return props.isToggleHidden && css(["border:0;clip:rect(0 0 0 0);height:1px;width:1px;margin:-1px;padding:0;overflow:hidden;position:absolute;"]);
}));
ToggleInput.displayName = 'ToggleInput';
export var LabelContainer = styled.div.withConfig({
  displayName: "styles__LabelContainer",
  componentId: "sc-14r4qak-4"
})(["& > *{", ";}& > *:first-child{line-height:", "px;}"], function (props) {
  return props.isDisabled && css(["opacity:0.5;cursor:not-allowed;"]);
}, function (props) {
  return props.size;
});
LabelContainer.displayName = 'LabelContainer';